import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  Warning,
  Stop,
  PlayArrow,
  PowerSettingsNew,
  Report,
} from '@mui/icons-material';
import api from '../services/api';
import { STATUS_POLL_MS } from '../config/intervals';

export default function EmergencyStop() {
  const [killSwitchStatus, setKillSwitchStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [action, setAction] = useState(null); // 'activate' or 'deactivate'

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showSuccess = (message) => {
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const showWarning = (message) => {
    setSnackbar({ open: true, message, severity: 'warning' });
  };

  const closeSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Fetch current kill switch status
  const fetchStatus = async () => {
    try {
      const response = await api.get('/api/emergency-kill-switch/status/');
      setKillSwitchStatus(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch kill switch status');
      console.error(err);
    }
  };

  useEffect(() => {
    fetchStatus();
    
    // Poll for status updates
    const interval = setInterval(fetchStatus, STATUS_POLL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleActivate = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/emergency-kill-switch/activate/', {
        reason: reason || 'Emergency stop activated from UI',
        activated_by: 'UI'
      });
      
      setKillSwitchStatus(response.data.data);
      setError(null);
      setConfirmDialog(false);
      setReason('');
      
      // Show success message
      showWarning(`EMERGENCY STOP ACTIVATED! ${response.data.triggers_stopped} triggers stopped. Reason: ${reason || 'Emergency stop'}`);
    } catch (err) {
      setError('Failed to activate kill switch');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/emergency-kill-switch/deactivate/');
      setKillSwitchStatus(response.data.data);
      setError(null);
      setConfirmDialog(false);
      
      showSuccess('Kill switch deactivated. You can now restart triggers manually.');
    } catch (err) {
      setError('Failed to deactivate kill switch');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (action === 'activate') {
      handleActivate();
    } else if (action === 'deactivate') {
      handleDeactivate();
    }
  };

  const isActive = killSwitchStatus?.is_active;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <Warning sx={{ mr: 2, color: 'error.main' }} />
        Emergency Kill Switch
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Status Card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            border: isActive ? '2px solid' : '1px solid',
            borderColor: isActive ? 'error.main' : 'divider',
            bgcolor: isActive ? 'error.light' : 'background.paper',
            color: isActive ? 'error.contrastText' : 'text.primary'
          }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                System Status
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Chip
                  icon={isActive ? <Stop /> : <PlayArrow />}
                  label={isActive ? 'EMERGENCY STOP ACTIVE' : 'System Normal'}
                  color={isActive ? 'error' : 'success'}
                  sx={{ fontSize: '1.2rem', py: 3, px: 2 }}
                />
              </Box>
              
              {killSwitchStatus && (
                <>
                  {isActive && killSwitchStatus.reason && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Reason:</strong> {killSwitchStatus.reason}
                    </Typography>
                  )}
                  {isActive && killSwitchStatus.activated_at && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Activated:</strong> {new Date(killSwitchStatus.activated_at).toLocaleString()}
                    </Typography>
                  )}
                  {isActive && killSwitchStatus.activated_by && (
                    <Typography variant="body2">
                      <strong>Activated By:</strong> {killSwitchStatus.activated_by}
                    </Typography>
                  )}
                  {!isActive && killSwitchStatus.deactivated_at && (
                    <Typography variant="body2">
                      <strong>Last Deactivated:</strong> {new Date(killSwitchStatus.deactivated_at).toLocaleString()}
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Control Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Emergency Controls
              </Typography>
              
              <Box sx={{ mt: 3 }}>
                {!isActive ? (
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    fullWidth
                    startIcon={<PowerSettingsNew />}
                    onClick={() => {
                      setAction('activate');
                      setConfirmDialog(true);
                    }}
                    disabled={loading}
                    sx={{ py: 2, fontSize: '1.1rem' }}
                  >
                    ACTIVATE EMERGENCY STOP
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    size="large"
                    fullWidth
                    startIcon={<PlayArrow />}
                    onClick={() => {
                      setAction('deactivate');
                      setConfirmDialog(true);
                    }}
                    disabled={loading}
                    sx={{ py: 2, fontSize: '1.1rem' }}
                  >
                    DEACTIVATE KILL SWITCH
                  </Button>
                )}
              </Box>

              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Warning:</strong> Activating the emergency stop will:
                </Typography>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Stop ALL active triggers immediately</li>
                  <li>Cancel all pending orders</li>
                  <li>Clear all capital reservations</li>
                  <li>Halt the entire arbitrage system</li>
                </ul>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Instructions */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Emergency Stop Instructions
            </Typography>
            
            <Typography variant="body1" paragraph>
              The Emergency Kill Switch is designed to immediately stop all arbitrage operations in case of:
            </Typography>
            
            <ul>
              <li>Critical bugs causing unexpected orders</li>
              <li>Market anomalies or flash crashes</li>
              <li>Exchange API issues or errors</li>
              <li>Security concerns or suspicious activity</li>
              <li>System maintenance requirements</li>
            </ul>

            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              <strong>To restart the system after emergency stop:</strong>
            </Typography>
            
            <ol>
              <li>Identify and resolve the issue that caused the emergency</li>
              <li>Deactivate the kill switch using the button above</li>
              <li>Manually restart desired triggers from the Triggers page</li>
              <li>Monitor system behavior closely after restart</li>
            </ol>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Telegram Commands:</strong> You can also control the emergency stop via Telegram:
              </Typography>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                <li><code>/stop</code> - Activate emergency stop</li>
                <li><code>/restart</code> - Deactivate kill switch and allow manual restart</li>
              </ul>
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => !loading && setConfirmDialog(false)}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <Report sx={{ mr: 1, color: action === 'activate' ? 'error.main' : 'success.main' }} />
          {action === 'activate' ? 'Activate Emergency Stop?' : 'Deactivate Kill Switch?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {action === 'activate' ? (
              <>
                This will immediately stop ALL arbitrage operations.
                All active triggers will be stopped and pending orders may be affected.
                <TextField
                  fullWidth
                  label="Reason for activation (optional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  multiline
                  rows={2}
                  margin="normal"
                  disabled={loading}
                />
              </>
            ) : (
              'This will deactivate the kill switch. You will need to manually restart any triggers you want to run.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog(false)} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color={action === 'activate' ? 'error' : 'success'}
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {action === 'activate' ? 'ACTIVATE STOP' : 'DEACTIVATE'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}